/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo, useState, VFC } from 'react';
import { Button, Col, DatePicker, Form, Modal, Row, Select, Space, Spin, Table, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { FileTextOutlined, SearchOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { useAuth } from '@isocel/front-common';

import { Store, InvoicePreviewRow } from '../../queries/api/types';
import { useStores } from '../../queries/store';
import { createInvoice, previewInvoice } from '../../queries/api/invoice';

import './FacturationManuelle.less';

const { Option } = Select;
const SELECT_ALL_VALUE = '__ALL__';

interface TableRow extends InvoicePreviewRow {
    key: string;
}

const FacturationManuelle: VFC = () => {
    const { user } = useAuth();
    const organizationId = user?.organization?.id;

    // --- Form state ---
    const [startDate, setStartDate] = useState<Moment | null>(null);
    const [endDate, setEndDate] = useState<Moment | null>(null);
    const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
    const [storeSearch, setStoreSearch] = useState('');

    // --- Data state ---
    const [rows, setRows] = useState<InvoicePreviewRow[]>([]);
    const [flowIds, setFlowIds] = useState<string[]>([]);
    const [previewed, setPreviewed] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const { data: storesData, isLoading: storesLoading } = useStores();

    // Only stores that belong to the current organization.
    const stores: Store[] = useMemo(() => {
        const all = storesData ?? [];
        if (!organizationId) return all;
        return all.filter((s) => {
            const orgId =
                typeof s.organization === 'string'
                    ? s.organization
                    : (s.organization as any)?.id ?? (s.organization as any)?._id;
            return orgId === organizationId;
        });
    }, [storesData, organizationId]);

    const filteredStores = useMemo(() => {
        const q = storeSearch.trim().toLowerCase();
        const list = q
            ? stores.filter((s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q))
            : stores;
        return [...list].sort((a, b) => {
            const numA = parseInt(a.code, 10);
            const numB = parseInt(b.code, 10);
            if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
            return a.code.localeCompare(b.code);
        });
    }, [stores, storeSearch]);

    // --- Date rules ---
    // Both dates are fully optional and independent.
    // startDate: must be strictly before today.
    // endDate:   must be strictly before today; if startDate is also set, must be strictly after startDate.
    const startOfToday = useMemo(() => moment().startOf('day'), []);

    const disabledStartDate = useCallback(
        (current: Moment | null) => {
            if (!current) return false;
            return current.isSameOrAfter(startOfToday);
        },
        [startOfToday]
    );

    const disabledEndDate = useCallback(
        (current: Moment | null) => {
            if (!current) return false;
            // Must be strictly before today.
            if (current.isSameOrAfter(startOfToday)) return true;
            // If startDate is set, endDate must be strictly after it.
            if (startDate && current.isSameOrBefore(startDate, 'day')) return true;
            return false;
        },
        [startOfToday, startDate]
    );

    const handleStartDateChange = (value: Moment | null) => {
        setStartDate(value);
        // If endDate is now ≤ the new startDate, clear it so the user must re-pick.
        if (value && endDate && endDate.isSameOrBefore(value, 'day')) {
            setEndDate(null);
        }
        setPreviewed(false);
        setRows([]);
        setFlowIds([]);
    };

    const handleEndDateChange = (value: Moment | null) => {
        setEndDate(value);
        setPreviewed(false);
        setRows([]);
        setFlowIds([]);
    };

    // --- Store selection with "select all" option ---
    const handleStoresChange = (values: string[]) => {
        if (values.includes(SELECT_ALL_VALUE)) {
            // If not everything selected yet -> select all, otherwise clear.
            const allIds = filteredStores.map((s) => s.id);
            const allAlreadySelected = allIds.length > 0 && allIds.every((id) => selectedStoreIds.includes(id));
            setSelectedStoreIds(allAlreadySelected ? [] : allIds);
        } else {
            setSelectedStoreIds(values);
        }
        setPreviewed(false);
        setRows([]);
        setFlowIds([]);
    };

    // --- Preview / Validate ---
    // Dates are optional — only the store selection is required.
    // When a date is not provided we fall back to an "all-time" range so the
    // backend required-field validation is always satisfied:
    //   startDate missing → 1970-01-01 (epoch, i.e. no lower-bound filter in practice)
    //   endDate   missing → end of today
    const effectiveStartDate = (d: Moment | null) =>
        d ? d.clone().startOf('day').toISOString() : moment(0).toISOString();
    const effectiveEndDate = (d: Moment | null) =>
        d ? d.clone().endOf('day').toISOString() : moment().subtract(1, 'day').endOf('day').toISOString();

    const canValidate = !!organizationId && selectedStoreIds.length > 0;

    const handleValidate = async () => {
        if (!canValidate || !organizationId) return;
        setPreviewLoading(true);
        setPreviewed(true);
        try {
            const response = await previewInvoice({
                organizationId,
                startDate: effectiveStartDate(startDate),
                endDate: effectiveEndDate(endDate),
                storeIds: selectedStoreIds,
            });
            setRows(response.rows ?? []);
            setFlowIds(response.flowIds ?? []);
        } catch (err: any) {
            const detail = err?.response?.data?.message || err?.response?.data || err?.message || String(err);
            message.error(
                `Erreur lors du calcul de la facturation : ${
                    typeof detail === 'string' ? detail : JSON.stringify(detail)
                }`
            );
            setRows([]);
            setFlowIds([]);
        } finally {
            setPreviewLoading(false);
        }
    };

    // --- Create invoice ---
    const handleSendInvoice = () => {
        if (!organizationId || rows.length === 0) return;

        Modal.confirm({
            title: 'Envoyer en facturation ?',
            content: `Une facture sera créée et ${flowIds.length} flux seront marqués comme facturés. Les compteurs magasin seront ajustés en fonction de la quantité à facturer (positive ou négative). Cette action est définitive.`,
            okText: 'Confirmer',
            cancelText: 'Annuler',
            onOk: async () => {
                setSubmitting(true);
                try {
                    const response = await createInvoice({
                        organizationId,
                        startDate: effectiveStartDate(startDate),
                        endDate: effectiveEndDate(endDate),
                        storeIds: selectedStoreIds,
                    });
                    message.success(
                        `Facture ${response.invoice?.name || response.invoice?._id} créée (${
                            response.details?.length ?? 0
                        } lignes, ${response.linkedFlowsCount ?? 0} flux liés).`
                    );
                    // Reset the screen after a successful submission.
                    setRows([]);
                    setFlowIds([]);
                    setPreviewed(false);
                } catch (err: any) {
                    const detail = err?.response?.data?.message || err?.response?.data || err?.message || String(err);
                    message.error(
                        `Erreur lors de l'envoi en facturation : ${
                            typeof detail === 'string' ? detail : JSON.stringify(detail)
                        }`
                    );
                } finally {
                    setSubmitting(false);
                }
            },
        });
    };

    // --- Table ---
    const tableData: TableRow[] = useMemo(
        () =>
            rows.map((r, idx) => ({
                ...r,
                key: `${r.storeId}-${r.packageId}-${idx}`,
            })),
        [rows]
    );

    const columns: ColumnsType<TableRow> = [
        {
            title: 'Magasin',
            key: 'store',
            render: (_: unknown, r: TableRow) => `${r.storeCode} - ${r.storeName}`,
            sorter: (a, b) =>
                (a.storeCode || '').localeCompare(b.storeCode || '', undefined, {
                    numeric: true,
                    sensitivity: 'base',
                }),
        },
        {
            title: 'Code article',
            dataIndex: 'packageEan',
            key: 'packageEan',
        },
        {
            title: 'Code support',
            dataIndex: 'packageInternCode',
            key: 'packageInternCode',
            sorter: (a, b) =>
                (a.packageInternCode || '').localeCompare(b.packageInternCode || '', undefined, {
                    numeric: true,
                    sensitivity: 'base',
                }),
        },
        {
            title: 'Libellé support',
            key: 'packageLabel',
            render: (_: unknown, r: TableRow) => {
                const t = r.packageTypeName || '';
                const m = r.packageModelName || '';
                if (t && m && t !== m) return `${t} + ${m}`;
                return t || m || '';
            },
        },
        {
            title: 'Quantité à facturer',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right',
            render: (value: number) => {
                const safe = Number.isFinite(value) ? value : 0;
                const cls = safe < 0 ? 'facturation-quantity negative' : 'facturation-quantity positive';
                const display = safe < 0 ? `- ${Math.abs(safe)}` : `${safe}`;
                return <span className={cls}>{display}</span>;
            },
            sorter: (a, b) => (a.quantity || 0) - (b.quantity || 0),
        },
    ];

    // --- Render ---
    const allFilteredSelected =
        filteredStores.length > 0 && filteredStores.every((s) => selectedStoreIds.includes(s.id));

    return (
        <div className="facturation-page">
            <h1>Facturation manuelle</h1>

            <div className="facturation-filters">
                <Form layout="vertical">
                    <Row gutter={16}>
                        <Col xs={24} sm={12} md={6} lg={6}>
                            <Form.Item label="Date de début" className="bold-label">
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    placeholder="Date de début"
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                    disabledDate={disabledStartDate}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6} lg={6}>
                            <Form.Item label="Date de fin" className="bold-label">
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    placeholder="Date de fin"
                                    value={endDate}
                                    onChange={handleEndDateChange}
                                    disabledDate={disabledEndDate}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item label="Magasin" className="bold-label" required>
                                <Select
                                    mode="multiple"
                                    showSearch
                                    placeholder="Sélectionner un ou plusieurs magasins"
                                    onSearch={setStoreSearch}
                                    filterOption={false}
                                    maxTagCount="responsive"
                                    suffixIcon={<SearchOutlined />}
                                    notFoundContent={storesLoading ? <Spin size="small" /> : 'Aucun résultat'}
                                    value={selectedStoreIds}
                                    onChange={handleStoresChange}
                                    onDropdownVisibleChange={(open) => {
                                        if (!open) setStoreSearch('');
                                    }}
                                >
                                    <Option key={SELECT_ALL_VALUE} value={SELECT_ALL_VALUE}>
                                        {allFilteredSelected ? 'Tout désélectionner' : 'Sélectionner tous les magasins'}
                                    </Option>
                                    {filteredStores.map((store) => (
                                        <Option key={store.id} value={store.id}>
                                            {store.code} - {store.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Space>
                                <Button
                                    type="primary"
                                    onClick={handleValidate}
                                    loading={previewLoading}
                                    disabled={!canValidate}
                                >
                                    Valider
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </div>

            {previewed && (
                <div className="facturation-results">
                    <div className="facturation-results-header">
                        <FileTextOutlined className="facturation-results-icon" />
                        <h3>Aperçu de la facturation</h3>
                    </div>

                    {previewLoading ? (
                        <div style={{ textAlign: 'center', padding: 24 }}>
                            <Spin />
                        </div>
                    ) : tableData.length > 0 ? (
                        <>
                            <Table columns={columns} dataSource={tableData} pagination={false} size="middle" bordered />
                            <div className="facturation-actions">
                                <Button type="primary" size="large" onClick={handleSendInvoice} loading={submitting}>
                                    Envoyer en facturation
                                </Button>
                            </div>
                        </>
                    ) : (
                        <p className="facturation-empty">
                            Aucun flux à facturer sur la période et les magasins sélectionnés.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default FacturationManuelle;
